/**
 * Spanish for the Tools screen (ADR 0325); see index.ts for the house style.
 * The subcontractor packet a crew signs is never in here: it stays English.
 */
export const tools: Readonly<Record<string, string>> = {
  // ------------------------------------------------------ the menu --
  soon: "pronto",
  "Not built yet.": "Aún no está construido.",
  "Company Docs": "Documentos de la empresa",
  Crews: "Cuadrillas",
  Checklists: "Listas de verificación",
  "Roof Templates": "Plantillas de techo",
  "Supplier Pricing": "Precios de proveedores",
  "Supplier Mail": "Correo de proveedores",
  SMS: "SMS",
  "Personal Texts": "Mensajes personales",
  Users: "Usuarios",
  Notifications: "Notificaciones",
  "Customer Links": "Enlaces para clientes",
  "Color Links": "Enlaces de colores",
  Employees: "Empleados",
  Estimates: "Presupuestos",
  Integrations: "Integraciones",
  Features: "Funciones",
  "Neighbour Letters": "Cartas a vecinos",
  "Lost Reasons": "Motivos de pérdida",
  "Lead Sources": "Fuentes de prospectos",
  "Custom Reports": "Informes personalizados",
  Products: "Productos",
  Suppliers: "Proveedores",
  "Proposal Upgrades": "Mejoras de la propuesta",
  "Change Order Terms": "Términos de orden de cambio",
  "Scope Disclaimers": "Avisos de alcance",
  "Product Videos": "Videos de productos",
  // ------------------------------------------- the plain table editor --
  Phase: "Fase",
  "Tab Order": "Orden de pestañas",
  "Also known as": "También conocido como",
  Category: "Categoría",
  Type: "Tipo",
  "Customer Wording": "Texto para el cliente",
  "Customer Scope": "Alcance para el cliente",
  "What the Customer Reads": "Lo que lee el cliente",
  "When the AI Adds It": "Cuándo lo agrega la IA",
  "Search…": "Buscar…",
  "1 record": "1 registro",
  "{n} records": "{n} registros",
  " · showing {n}": " · mostrando {n}",
  "Show more": "Mostrar más",
  "Changing Cost recalculates Price at cost ÷ 0.6 (40% gross profit).":
    "Cambiar el costo recalcula el precio como costo ÷ 0.6 (40% de utilidad bruta).",
  "Names cannot be changed — jobs and documents store this value as text, so renaming would orphan them. Add a new one and deactivate the old.":
    "Los nombres no se pueden cambiar: los trabajos y documentos guardan este valor como texto, y cambiarlo los dejaría huérfanos. Agrega uno nuevo y desactiva el anterior.",
  // The thing being added carries its own article, so each noun gets its
  // gender: "a product" / "the new lead source".
  "Name {thing}": "Nombre para {thing}",
  'There is already {thing} called "{name}". Search for it above to edit it, or add this one under a different name.':
    'Ya existe {thing} con el nombre "{name}". Usa la búsqueda de arriba para editar ese registro, o agrega este con otro nombre.',
  "a product": "un producto",
  "a employee": "un empleado",
  "a lost reason": "un motivo de pérdida",
  "a lead source": "una fuente de prospectos",
  "a change order term": "un término de orden de cambio",
  "a scope disclaimer": "un aviso de alcance",
  "a trade": "un oficio",
  "the new product": "el nuevo producto",
  "the new employee": "el nuevo empleado",
  "the new lost reason": "el nuevo motivo de pérdida",
  "the new lead source": "la nueva fuente de prospectos",
  "the new change order term": "el nuevo término de orden de cambio",
  "the new scope disclaimer": "el nuevo aviso de alcance",
  "the new trade": "el nuevo oficio",
  // --------------------------------------------------------- Users --
  "Super admin": "Superadministrador",
  Admin: "Administrador",
  Tester: "Probador",
  "Agent (data access)": "Agente (acceso a datos)",
  "Agent · read-only": "Agente · solo lectura",
  "Agent · prospector": "Agente · prospector",
  "Agent · coordinator": "Agente · coordinador",
  "Agent · comms": "Agente · comunicaciones",
  "Agent · estimator": "Agente · presupuestos",
  "Holds every role, including any added later. Only a super admin can grant it (ADR 0152).":
    "Tiene todos los roles, incluidos los que se agreguen después. Solo un superadministrador puede otorgarlo (ADR 0152).",
  "Full access to everything.": "Acceso completo a todo.",
  "Sees Test jobs and can mark one. Everyone without it never sees them (ADR 0151).":
    "Ve los trabajos de prueba y puede marcar uno. Quien no lo tiene nunca los ve (ADR 0151).",
  "Lets an AI agent read and write through the MCP server.":
    "Permite a un agente de IA leer y escribir a través del servidor MCP.",
  "MCP: queries and reports only.": "MCP: solo consultas e informes.",
  "MCP: reads, plus creating prospects — new customers and jobs only.":
    "MCP: lectura, más crear prospectos: solo clientes y trabajos nuevos.",
  "MCP: reads, notes and calls, checklist steps, inspection and work dates. No production handoff.":
    "MCP: lectura, notas y llamadas, pasos de la lista de verificación, fechas de inspección y de trabajo. Sin entrega a producción.",
  "MCP: notes, checklists, scheduling, new leads.":
    "MCP: notas, listas de verificación, programación, prospectos nuevos.",
  "MCP: estimates, orders and expenses — money-mutating.":
    "MCP: presupuestos, órdenes y gastos; modifica dinero.",
  "Only a super admin can grant or remove the super admin role.":
    "Solo un superadministrador puede otorgar o quitar el rol de superadministrador.",
  "You cannot remove your own super admin role.":
    "No es posible quitar el propio rol de superadministrador.",
  "You cannot remove your own admin role.":
    "No es posible quitar el propio rol de administrador.",
  "User management is unavailable: {message}. Deploy the manage-users Edge Function (see supabase/functions/manage-users).":
    "La administración de usuarios no está disponible: {message}. Hay que desplegar la Edge Function manage-users (ver supabase/functions/manage-users).",
  "Admins only": "Solo administradores",
  "Ask an admin to change who can sign in.":
    "Pide a un administrador que cambie quién puede iniciar sesión.",
  "1 person": "1 persona",
  "{n} people": "{n} personas",
  "+ Invite user": "+ Invitar usuario",
  "Sign-in": "Inicio de sesión",
  Roles: "Roles",
  "Last sign-in": "Último inicio de sesión",
  "no login": "sin acceso",
  deactivated: "desactivado",
  active: "activo",
  "Deactivate {name}? They are signed out immediately and cannot sign back in.":
    "¿Desactivar a {name}? Se cierra su sesión de inmediato y no podrá volver a entrar.",
  Reactivate: "Reactivar",
  Deactivate: "Desactivar",
  "Roles are carried in the sign-in token and decide what the database will return. A change takes effect the next time that person's session refreshes; deactivating takes effect immediately.":
    "Los roles viajan en el token de inicio de sesión y deciden qué devuelve la base de datos. Un cambio surte efecto la próxima vez que se actualice la sesión de esa persona; desactivar surte efecto de inmediato.",
  "Invite user": "Invitar usuario",
  "Sends a Supabase invite email. They set their own password — nobody here ever sees it.":
    "Envía un correo de invitación de Supabase. La persona elige su propia contraseña; nadie aquí la ve nunca.",
  "Send invite": "Enviar invitación",
  "Full name": "Nombre completo",
  // --------------------------------------------------------- Crews --
  "1 crew": "1 cuadrilla",
  "{n} crews": "{n} cuadrillas",
  "+ Create": "+ Crear",
  "Can login": "Puede iniciar sesión",
  "In house": "Personal propio",
  "Nail gun": "Clavadora",
  Documents: "Documentos",
  "Labor costs": "Costos de mano de obra",
  "No crews.": "No hay cuadrillas.",
  "Our own employees — never paid out as a crew":
    "Empleados propios: nunca se les paga como cuadrilla",
  "{name} nail gun": "Clavadora de {name}",
  Coil: "Rollo",
  "{n} Trades": "{n} oficios",
  "Certificates and W-9s, and the fields they fill in":
    "Certificados y W-9, y los campos que llenan",
  "Email them the W-9, bank, insurance and agreement to fill in and sign":
    "Enviarles por correo el W-9, datos bancarios, seguro y contrato para llenar y firmar",
  "Send packet": "Enviar paquete",
  "Delete this crew?": "¿Eliminar esta cuadrilla?",
  "WC to {date}": "WC hasta {date}",
  "no WC": "sin WC",
  "GL to {date}": "GL hasta {date}",
  "no GL": "sin GL",
  // A crew's cover, as `coverageLabel` in packages/core says it
  "Expired yesterday": "Venció ayer",
  "Expired {n} days ago": "Venció hace {n} días",
  "Expires today": "Vence hoy",
  "Expires tomorrow": "Vence mañana",
  "Expires in {n} days": "Vence en {n} días",
  "Not covered": "Sin cobertura",
  "No expiry on file": "Sin fecha de vencimiento registrada",
  "Not recorded": "Sin registrar",
  "Covered to {date}": "Cubierta hasta {date}",
  Covered: "Cubierta",
  // ------------------------------------------------------ New crew --
  "Give the crew a name.": "Ponle nombre a la cuadrilla.",
  "New crew": "Nueva cuadrilla",
  "Insurance and documents are added afterwards, from the crew's Documents button.":
    "El seguro y los documentos se agregan después, desde el botón Documentos de la cuadrilla.",
  "Create crew": "Crear cuadrilla",
  "The company — or the person, if it is just them":
    "La empresa, o la persona si trabaja sola",
  "Who to call there": "A quién llamar ahí",
  "No trades set up yet.": "Aún no hay oficios configurados.",
  Settings: "Configuración",
  "Working for us now": "Trabaja con nosotros ahora",
  "Signs in to see their own work orders":
    "Inicia sesión para ver sus propias órdenes de trabajo",
  "Our own employee — costed, never paid out":
    "Empleado propio: se costea, nunca se le paga como cuadrilla",
  // ------------------------------------------------ Crew documents --
  "Workers' comp": "Compensación de trabajadores",
  "Workers' comp expires": "Vence la compensación de trabajadores",
  "General liability": "Responsabilidad civil",
  "General liability expires": "Vence la responsabilidad civil",
  Insurer: "Aseguradora",
  "Legal name": "Nombre legal",
  "Tax ID type": "Tipo de número fiscal",
  Yes: "Sí",
  No: "No",
  "Delete “{name}” for good? The file goes with it, and nothing here can put it back.":
    "¿Eliminar “{name}” para siempre? El archivo se va con él, y nada aquí puede recuperarlo.",
  "{crew} — documents": "{crew} — documentos",
  "Certificates and W-9s. Uploading one fills these fields in for you to check.":
    "Certificados y W-9. Al subir uno, estos campos se llenan para que los revises.",
  " — ask this crew for a current certificate.":
    " — pide a esta cuadrilla un certificado vigente.",
  "What we hold": "Lo que tenemos",
  "Carries it": "La tiene",
  "Does not": "No la tiene",
  "Tax ID on file": "Número fiscal registrado",
  "EIN on file": "EIN registrado",
  "Social Security number on file": "Número de Seguro Social registrado",
  Social: "Seguro Social",
  "Which kind of number the W-9 gives. The number itself is never stored — it stays on the document.":
    "Qué tipo de número da el W-9. El número en sí nunca se guarda: se queda en el documento.",
  "Add a document": "Agregar un documento",
  "It is a": "Es un",
  "Document type": "Tipo de documento",
  "Certificate of insurance": "Certificado de seguro",
  "W-9": "W-9",
  Licence: "Licencia",
  "Reading it…": "Leyéndolo…",
  "A PDF or a photograph. It is saved first, then read.":
    "Un PDF o una fotografía. Primero se guarda y luego se lee.",
  "What the document says": "Lo que dice el documento",
  "Nothing has been written to this crew yet. Check it, then keep it.":
    "Todavía no se ha escrito nada en esta cuadrilla. Revísalo y luego consérvalo.",
  Field: "Campo",
  Reading: "Lectura",
  "check this": "revisar",
  "Keep these": "Conservar estos",
  "Keep the file only": "Conservar solo el archivo",
  "On file": "Archivados",
  "Nothing uploaded yet.": "Aún no se ha subido nada.",
  Uploaded: "Subido",
  File: "Archivo",
  Expires: "Vence",
  "Delete {name}": "Eliminar {name}",
  "Delete it and its file for good":
    "Eliminarlo junto con su archivo para siempre",
  // ------------------------------------------- Subcontractor packet --
  "Send the packet to {name}": "Enviar el paquete a {name}",
  "In Spanish. They can switch to English on the page.":
    "En español. Pueden cambiar a inglés en la página.",
  "In English. They can switch to Spanish on the page.":
    "En inglés. Pueden cambiar a español en la página.",
  "{name} has no email address on file. Type one in below, or copy the message and text it to them.":
    "{name} no tiene correo registrado. Escribe uno abajo, o copia el mensaje y envíaselo por texto.",
  "Subcontractor packet for {name}": "Paquete de subcontratista para {name}",
  "W-9, bank account, insurance and the subcontractor agreement — filled in and signed on their phone, and filed here when they sign.":
    "W-9, cuenta bancaria, seguro y el contrato de subcontratista: se llenan y firman en su teléfono, y se archivan aquí cuando firman.",
  "Which language should it open in?": "¿En qué idioma debe abrirse?",
  English: "Inglés",
  Spanish: "Español",
  "Already made": "Ya creados",
  "Opened, not signed": "Abierto, sin firmar",
  "Link made, not sent": "Enlace creado, sin enviar",
  "Expired unsigned": "Venció sin firmar",
  " by {name}, {when} — the PDF is in Documents":
    " por {name}, {when} — el PDF está en Documentos",
  // ----------------------------------------------------- Suppliers --
  "1 supplier": "1 proveedor",
  "{n} suppliers": "{n} proveedores",
  Abbr: "Abrev.",
  Locations: "Sucursales",
  "Price sheet": "Lista de precios",
  "On account": "A crédito",
  "Is active": "Activo",
  "Primary supplier contact": "Contacto principal del proveedor",
  "Order email": "Email para pedidos",
  "QM credit": "Crédito QM",
  "No suppliers.": "No hay proveedores.",
  "{n} Products": "{n} productos",
  "{n} Users": "{n} usuarios",
  "{n} locations": "{n} sucursales",
  "Catalogue cost": "Costo de catálogo",
  "Delete this supplier?": "¿Eliminar este proveedor?",
  "Supplier names cannot be changed — material orders reference them. Deactivate instead. On account means the company is invoiced here rather than paying at the till, so their purchases never appear in Receipts — the cost already reaches the job as a material order. Order email is where Place Material Order sends to. QM credit means this supplier credits our account for a QuickMeasure report when we buy the material from them; the order email asks for it. Counts fall back to the imported figures until products, contacts and branches are linked here. The price sheet decides what this supplier's materials cost when an estimate is generated; suppliers on the same sheet are quoted alike.":
    "Los nombres de proveedores no se pueden cambiar: las órdenes de materiales los referencian. Mejor desactívalo. A crédito significa que a la empresa se le factura aquí en lugar de pagar en caja, así que sus compras nunca aparecen en Recibos; el costo ya llega al trabajo como orden de materiales. El email para pedidos es adonde envía Colocar orden de materiales. Crédito QM significa que este proveedor abona a nuestra cuenta un informe de QuickMeasure cuando le compramos el material; el correo del pedido lo solicita. Los conteos usan las cifras importadas hasta que aquí se vinculen productos, contactos y sucursales. La lista de precios decide cuánto cuestan los materiales de este proveedor al generar un presupuesto; los proveedores en la misma lista se cotizan igual.",
  // ------------------------------------------------ Supplier pricing --
  '"{value}" is not a price.': '"{value}" no es un precio.',
  "{priced} priced · {n} products": "{priced} con precio · {n} productos",
  "Name the new price sheet": "Nombre para la nueva lista de precios",
  "+ New price sheet": "+ Nueva lista de precios",
  "Used by {names}.": "La usan {names}.",
  "No supplier reads off this sheet yet — set one under Tools → Suppliers.":
    "Ningún proveedor usa esta lista todavía; asigna uno en Herramientas → Proveedores.",
  "A blank price means this supplier has not quoted the product, and the catalogue cost is used instead.":
    "Un precio en blanco significa que este proveedor no ha cotizado el producto, y se usa el costo de catálogo.",
  "This supplier": "Este proveedor",
  "No products.": "No hay productos.",
  // ------------------------------------------------ Proposal upgrades --
  '{n} estimate(s) list "{name}" as already included. Renaming it means those estimates will charge for it instead. Rename anyway?':
    '{n} presupuesto(s) indican "{name}" como ya incluida. Si cambias el nombre, esos presupuestos la cobrarán. ¿Cambiar el nombre de todos modos?',
  "1 upgrade": "1 mejora",
  "{n} upgrades": "{n} mejoras",
  "+ Add upgrade": "+ Agregar mejora",
  "These read under the packages on every proposal, in this order. Leave the measurement blank for a flat price; set one and the price is per unit, worked out from the roof.":
    "Estas aparecen debajo de los paquetes en cada propuesta, en este orden. Deja la medida en blanco para un precio fijo; ponle una y el precio es por unidad, calculado a partir del techo.",
  "Measured against": "Se mide contra",
  Covers: "Cubre",
  Offered: "Se ofrece",
  "No upgrades. Add one and it appears on every proposal.":
    "No hay mejoras. Agrega una y aparece en cada propuesta.",
  " per {unit}": " por {unit}",
  unit: "unidad",
  " flat": " fijo",
  "Needs a price before it can be offered.":
    "Necesita un precio antes de poder ofrecerse.",
  "Inactive, so it is not offered.": "Inactiva, así que no se ofrece.",
  "Stop offering it on proposals. The product stays in the catalogue.":
    "Dejar de ofrecerla en las propuestas. El producto se queda en el catálogo.",
  "Take off": "Quitar",
  // ------------------------------------------------- Roof templates --
  "Click a template to add the products that go on it. A template with no products generates nothing. Mark one as finished from inside it, and the badge comes off everywhere.":
    "Haz clic en una plantilla para agregar los productos que lleva. Una plantilla sin productos no genera nada. Márcala como terminada desde adentro y la etiqueta desaparece en todas partes.",
  "Roof Type": "Tipo de techo",
  "1 product": "1 producto",
  "{n} products": "{n} productos",
  empty: "vacía",
  this: "esto",
  "Put {n} of {name} on every job from this template?":
    "¿Poner {n} de {name} en cada trabajo de esta plantilla?",
  "One {per} of {name} covers {n} {unit}?":
    "¿Un {per} de {name} cubre {n} {unit}?",
  "This is the product's own figure, so it changes every template that uses it and every estimate generated from now on. Estimates already written are not touched.":
    "Esta cifra es del producto mismo, así que cambia cada plantilla que lo usa y cada presupuesto generado de ahora en adelante. Los presupuestos ya escritos no se tocan.",
  "1 {per} per": "1 {per} por",
  "A set count, whatever the roof measures.":
    "Una cantidad fija, mida lo que mida el techo.",
  "How much one {per} covers.": "Cuánto cubre un {per}.",
  "per job": "por trabajo",
  "(removed)": "(eliminado)",
  "← All templates": "← Todas las plantillas",
  "no trade": "sin oficio",
  " · empty": " · vacía",
  "Finished — safe to estimate from. Leave this off while you are still adding products, and the app shows it as {label}.":
    "Terminada: segura para presupuestar. Déjalo desmarcado mientras sigas agregando productos, y la app la muestra como {label}.",
  "a set count per job": "una cantidad fija por trabajo",
  "quantity from {measurement}": "cantidad según {measurement}",
  // A slot is "<part> products" or "<part> products ea" (ROOF_SLOTS and
  // WALL_SLOTS in core); the part is translated on its own
  "{part} products": "Productos: {part}",
  "{part} products ea": "Productos c/u: {part}",
  Eave: "Alero",
  Rake: "Borde inclinado",
  Ridge: "Cumbrera",
  "Ridge vent": "Ventila de cumbrera",
  Hip: "Lima",
  "Hip & ridge": "Lima y cumbrera",
  Gutter: "Canalón",
  Valley: "Limahoya",
  Flashing: "Tapajuntas",
  "Step flashing": "Tapajuntas escalonado",
  Transition: "Transición",
  "Flat area field": "Campo de área plana",
  "Flat membrane": "Membrana plana",
  "Flat insulation": "Aislamiento plano",
  "Open edge": "Borde abierto",
  "Parapet wall": "Muro de parapeto",
  "Flat area perimeter": "Perímetro de área plana",
  "Siding tear-off": "Desmontaje de revestimiento",
  "House wrap": "Membrana para muros",
  "Outside corner": "Esquina exterior",
  "Inside corner": "Esquina interior",
  "J channel": "Canal J",
  "Siding starter": "Inicio de revestimiento",
  "Window & door": "Ventanas y puertas",
  "Soffit (by the foot)": "Sofito (por pie)",
  "Soffit (by area)": "Sofito (por área)",
  Fascia: "Fascia",
  "Frieze board": "Tabla de friso",
  // The slots that are not a pair
  "Flat membrane products (roof + walls)":
    "Productos: membrana plana (techo + muros)",
  "Flat deck products (roof only)": "Productos: cubierta plana (solo techo)",
  "Open edge products (eave + rake)":
    "Productos: borde abierto (alero + borde inclinado)",
  "Steep charge 8/12 - 12/12": "Cargo por pendiente 8/12 - 12/12",
  "Steep charge 13/12 and greater": "Cargo por pendiente 13/12 y mayor",
  "Tear-off and disposal": "Desmontaje y desecho",
  "Extra layers (each layer after the first)":
    "Capas adicionales (cada capa después de la primera)",
  // The parts of a deck (DECK_SLOTS in core)
  "Footings and posts": "Zapatas y postes",
  Framing: "Estructura",
  "Deck boards": "Tablas del deck",
  Railing: "Barandal",
  Steps: "Escalones",
  "Deck labor": "Mano de obra del deck",
  // What a slot's quantity comes from (RoofSlotMeasurement, underscores
  // read as spaces)
  squares: "cuadros",
  "eave lf": "pies lineales de alero",
  "rake lf": "pies lineales de borde inclinado",
  "ridge lf": "pies lineales de cumbrera",
  "ridge vent lf": "pies lineales de ventila de cumbrera",
  "hip lf": "pies lineales de lima",
  "hip ridge lf": "pies lineales de lima y cumbrera",
  "gutter lf": "pies lineales de canalón",
  "valley lf": "pies lineales de limahoya",
  "flashing lf": "pies lineales de tapajuntas",
  "step flashing lf": "pies lineales de tapajuntas escalonado",
  "transition lf": "pies lineales de transición",
  "flat squares": "cuadros planos",
  "flat membrane squares": "cuadros de membrana plana",
  "flat insulation squares": "cuadros de aislamiento plano",
  "flat deck squares": "cuadros de cubierta plana",
  "open edge lf": "pies lineales de borde abierto",
  "wall lf": "pies lineales de muro",
  "flat perimeter lf": "pies lineales de perímetro plano",
  "steep mid squares": "cuadros de pendiente media",
  "steep high squares": "cuadros de pendiente alta",
  "tearoff squares": "cuadros de desmontaje",
  "extra layer squares": "cuadros de capa adicional",
  "siding squares": "cuadros de revestimiento",
  "siding removal squares": "cuadros de desmontaje de revestimiento",
  "house wrap squares": "cuadros de membrana para muros",
  "outside corner posts": "postes de esquina exterior",
  "inside corner posts": "postes de esquina interior",
  "j channel lf": "pies lineales de canal J",
  "siding starter lf": "pies lineales de inicio de revestimiento",
  "openings ea": "ventanas y puertas c/u",
  "soffit lf": "pies lineales de sofito",
  "soffit squares": "cuadros de sofito",
  "fascia lf": "pies lineales de fascia",
  "frieze lf": "pies lineales de tabla de friso",
  // ------------------------------------------------- Product videos --
  "That is an iPhone .mov, which many Android phones and Windows computers will not play. Product videos have to be MP4 — export or share it as MP4 (CapCut does this) and try again.":
    "Es un .mov de iPhone, que muchos teléfonos Android y computadoras Windows no reproducen. Los videos de productos tienen que ser MP4: expórtalo o compártelo como MP4 (CapCut lo hace) e inténtalo de nuevo.",
  "That does not look like a video. Product videos have to be MP4 files.":
    "Eso no parece un video. Los videos de productos tienen que ser archivos MP4.",
  "The still has to be a JPEG, PNG or WebP image.":
    "La imagen fija tiene que ser JPEG, PNG o WebP.",
  "Take the walkthrough off {name}? Proposals presented from now on will not show it. Proposals already sent keep playing it — the file itself is kept.":
    "¿Quitar el recorrido de {name}? Las propuestas presentadas de ahora en adelante no lo mostrarán. Las ya enviadas lo siguen reproduciendo; el archivo se conserva.",
  "1 product with a video": "1 producto con video",
  "{n} products with a video": "{n} productos con video",
  "A product's walkthrough plays on the customer's proposal, directly under their inspection photographs, on every estimate that sells it. Keep them short and around 15 MB — a customer opens these on a phone. The still is taken from the video itself unless you upload one.":
    "El recorrido de un producto se reproduce en la propuesta del cliente, justo debajo de sus fotografías de inspección, en cada presupuesto que lo vende. Mantenlos cortos y de unos 15 MB: el cliente los abre en el teléfono. La imagen fija se toma del propio video a menos que subas una.",
  "“See Our Work” link": "Enlace “See Our Work”",
  "The last page of every residential proposal, just above where the customer signs: one button that opens this. Leave it empty and that page is not shown at all. Commercial proposals never show it.":
    "La última página de cada propuesta residencial, justo arriba de donde firma el cliente: un botón que abre esto. Déjalo vacío y esa página no se muestra. Las propuestas comerciales nunca la muestran.",
  "Choose a video for {name}": "Elegir un video para {name}",
  "Pick the product this video is of.":
    "Elige el producto del que trata este video.",
  Still: "Imagen fija",
  "What the customer reads over it": "Lo que lee el cliente encima",
  "The line underneath": "La línea de abajo",
  "No product has a walkthrough yet.":
    "Ningún producto tiene recorrido todavía.",
  "Still from the {name} walkthrough": "Imagen fija del recorrido de {name}",
  "No still": "Sin imagen fija",
  "Replace still": "Reemplazar imagen fija",
  "Watch it": "Verlo",
  "A short walkthrough of the work, start to finish.":
    "Un recorrido breve del trabajo, de principio a fin.",
  // --------------------------------------------------- Company docs --
  "Delete “{name}” for good? Retiring it instead keeps it on everything it has already gone out with.":
    "¿Eliminar “{name}” para siempre? Si en cambio lo retiras, se queda en todo lo que ya salió con él.",
  "Upload document": "Subir documento",
  "Licence, insurance, W-9, warranties — anything sent over and over.":
    "Licencia, seguro, W-9, garantías: todo lo que se envía una y otra vez.",
  "1 document. Pick from these on an estimate.":
    "1 documento. Elige entre estos en un presupuesto.",
  "{n} documents. Pick from these on an estimate.":
    "{n} documentos. Elige entre estos en un presupuesto.",
  "Show retired": "Mostrar retirados",
  "Nothing here yet.": "Aún no hay nada aquí.",
  "Retiring a document takes it out of the picker and leaves it on everything already sent. Deleting one outright is Lysa, Sam and Jonathon only.":
    "Retirar un documento lo saca del selector y lo deja en todo lo ya enviado. Eliminar uno por completo es solo para Lysa, Sam y Jonathon.",
  "Expired {date}": "Venció el {date}",
  "Expires in 1 day": "Vence en 1 día",
  "Licence, Insurance…": "Licencia, Seguro…",
  "Take it out of the picker, leave it on what was already sent":
    "Sacarlo del selector y dejarlo en lo ya enviado",
  "Put it back in the picker": "Devolverlo al selector",
  Retire: "Retirar",
  // ---------------------------------------------------- Color links --
  "This browser would not let us copy. Select the link instead.":
    "Este navegador no permitió copiar. Selecciona el enlace en su lugar.",
  "Copy the {label} link": "Copiar el enlace de {label}",
  "Text or email one of these to a customer. Each opens the same colors the card on a proposal opens, and nobody needs to sign in.":
    "Envía uno de estos a un cliente por texto o correo. Cada uno abre los mismos colores que abre la tarjeta en una propuesta, y nadie necesita iniciar sesión.",
  "Every color option": "Todas las opciones de color",
  // ------------------------------------------------------ Features --
  "Referral partners: texts, referrals and storm alerts":
    "Socios de referidos: mensajes, referidos y alertas de tormenta",
  "The Partners tab is always there as the business card file: scan a card, and see every card and what line of work its owner is in. This switch adds the rest of the partner network on top — a monthly text from the rep’s own phone, a text after a storm, a Partners card on a job, and a record of which referral turned into a roof. Nothing has been deleted, and turning it off deletes nothing.":
    "La pestaña Socios siempre está como archivo de tarjetas de presentación: escanea una tarjeta y ve cada tarjeta y a qué se dedica su dueño. Este interruptor agrega encima el resto de la red de socios: un mensaje mensual desde el teléfono del vendedor, un mensaje después de una tormenta, una tarjeta de Socios en un trabajo y un registro de qué referido se convirtió en techo. No se ha eliminado nada, y apagarlo no elimina nada.",
  "Partners shows the whole network again: texts, referrals and storms. Reload to see it.":
    "Socios vuelve a mostrar toda la red: mensajes, referidos y tormentas. Recarga para verlo.",
  "Partners is back to the business card file alone. Nothing was deleted. Reload to see it.":
    "Socios vuelve a ser solo el archivo de tarjetas de presentación. No se eliminó nada. Recarga para verlo.",
  "Quote check on a material order":
    "Verificación de cotización en una orden de materiales",
  "The panel at the top of a material order that compares it line by line with the estimate it came from, lists every difference with the quoted number beside the ordered one, and offers to put each one back. Off since 2026-09-21: it works, but on a real order it listed fourteen differences of which only a couple were mistakes, and a warning that is usually wrong is a warning people scroll past. While it is off, an order is not checked, nothing is asked of Jev, and placing an order says nothing about the quote. Nothing has been deleted — ticking this puts the panel back, and every review already recorded is still there.":
    "El panel en la parte superior de una orden de materiales que la compara línea por línea con el presupuesto del que salió, enumera cada diferencia con la cantidad cotizada junto a la ordenada y ofrece restaurar cada una. Apagado desde el 2026-09-21: funciona, pero en una orden real enumeró catorce diferencias de las que solo un par eran errores, y una advertencia que casi siempre está mal es una advertencia que la gente pasa de largo. Mientras está apagado, una orden no se verifica, no se le pide nada a Jev, y colocar una orden no dice nada sobre la cotización. No se ha eliminado nada: marcar esto devuelve el panel, y cada revisión ya registrada sigue ahí.",
  "Material orders are checked against their quote again. Reopen an order to see it.":
    "Las órdenes de materiales se verifican de nuevo contra su cotización. Vuelve a abrir una orden para verlo.",
  "The quote panel is gone from every material order. Nothing was deleted. Reopen an order to see it.":
    "El panel de cotización desapareció de todas las órdenes de materiales. No se eliminó nada. Vuelve a abrir una orden para verlo.",
  "Only an admin can switch a section on.":
    "Solo un administrador puede activar una sección.",
};
